import { useEffect, useState } from "react";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { fetchAutopilot, saveAutopilot, runAutopilotNow } from "@/lib/api";
import { errorMessage } from "@/lib/errorMessage";

const CHANNEL_STYLES = [
  { key: "facebook", label: "Facebook" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "pinterest", label: "Pinterest" },
  { key: "shorts", label: "Shorts / TikTok" },
];

export const AutopilotCard = ({ pass, channels }) => {
  const [settings, setSettings] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchAutopilot(pass)
      .then((r) => {
        setSettings(r.settings);
        setLastRun(r.last_run);
      })
      .catch(() => {});
  }, [pass]);

  if (!settings) return null;

  const patch = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async (next) => {
    setSaving(true);
    try {
      const body = next || settings;
      const r = await saveAutopilot(pass, {
        enabled: body.enabled,
        buffer_channel_id: body.buffer_channel_id || "",
        channel: body.channel,
        tone: body.tone,
        max_sources: Number(body.max_sources) || 5,
      });
      setSettings(r.settings);
      toast.success(r.settings.enabled ? "Autopilot ON — runs every Monday 6am UTC." : "Autopilot saved (off).");
    } catch (e) {
      toast.error(errorMessage(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runAutopilotNow(pass);
      setLastRun(r);
      if (r.errors?.length) toast.error(r.errors[0]);
      else if (r.skipped) toast.info(`Skipped: ${r.skipped}`);
      else toast.success(`Autopilot pushed ${r.pushed} posts to Buffer.`);
    } catch (e) {
      toast.error(errorMessage(e, "Run failed."));
    } finally {
      setRunning(false);
    }
  };

  const selCls =
    "w-full bg-white border border-[#E5E2DC] rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#2B5339]";

  return (
    <div data-testid="autopilot-card" className="mt-6 bg-white border border-[#E5E2DC] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold flex items-center gap-1.5">
            <Zap size={11} className="text-[#2B5339]" /> Autopilot
          </div>
          <p className="text-xs text-[#5C6B62] mt-1">
            Every Monday at 6am UTC, drafts posts for sources not published in 14 days and
            queues them to Buffer — no button press needed.
          </p>
        </div>
        <button
          data-testid="autopilot-toggle"
          type="button"
          disabled={saving}
          onClick={() => save({ ...settings, enabled: !settings.enabled })}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            settings.enabled ? "bg-[#2B5339]" : "bg-[#D5D2CC]"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
              settings.enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#5C6B62]">Buffer channel</span>
          <select
            data-testid="autopilot-channel-select"
            className={selCls}
            value={settings.buffer_channel_id || ""}
            onChange={(e) => patch("buffer_channel_id", e.target.value)}
          >
            <option value="">First connected</option>
            {(channels?.channels || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {c.service}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#5C6B62]">Copy style</span>
          <select
            data-testid="autopilot-style-select"
            className={selCls}
            value={settings.channel}
            onChange={(e) => patch("channel", e.target.value)}
          >
            {CHANNEL_STYLES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#5C6B62]">Tone</span>
          <select
            data-testid="autopilot-tone-select"
            className={selCls}
            value={settings.tone}
            onChange={(e) => patch("tone", e.target.value)}
          >
            <option value="warm">Warm</option>
            <option value="punchy">Punchy</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-[#5C6B62]">Max posts / week</span>
          <input
            data-testid="autopilot-max-input"
            type="number"
            min={1}
            max={10}
            className={selCls}
            value={settings.max_sources}
            onChange={(e) => patch("max_sources", e.target.value)}
          />
        </label>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div data-testid="autopilot-last-run" className="text-[10px] text-[#5C6B62]">
          {lastRun
            ? `Last run ${new Date(lastRun.run_at).toLocaleString()} — pushed ${lastRun.pushed ?? 0}${
                lastRun.errors?.length ? ` · ${lastRun.errors.length} error(s)` : ""
              }${lastRun.skipped ? ` · skipped: ${lastRun.skipped}` : ""}`
            : "Never run yet."}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="autopilot-save-btn"
            type="button"
            disabled={saving}
            onClick={() => save()}
            className="text-xs px-3 py-1.5 rounded-full border border-[#2B5339] text-[#2B5339] hover:bg-[#F6FAF7] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
          <button
            data-testid="autopilot-run-btn"
            type="button"
            disabled={running}
            onClick={runNow}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1C2B23] text-white hover:bg-[#2B5339] disabled:opacity-50"
          >
            {running ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
            Run now
          </button>
        </div>
      </div>
    </div>
  );
};
